<script setup lang="ts">
import { useNumberField } from '@/modules/Project/useNumberField'
import { normaliseLocalisedNumberString } from '@/shared/utils/string'
import MarkdownWithGroundingClaims from '@/sharedComponents/MarkdownWithGroundingClaims/MarkdownWithGroundingClaims.vue'
import { twMerge } from 'tailwind-merge'
import { computed, useTemplateRef } from 'vue'
import type { SidebarEmits } from '../../field-types'
import { type SidebarProps } from '../../field-types'
import { useFieldClaimsAndSources } from '../../useFieldClaimsAndSources'

const props = defineProps<SidebarProps<'number'>>()
const emit = defineEmits<SidebarEmits<'number'>>()

const inputRef = useTemplateRef<HTMLInputElement>('inputRef')

const { inputFieldValue, shouldBeRed } = useNumberField(
  computed(() => props.field),
  computed(() => props.property),
)

function onChange() {
  const normalized = normaliseLocalisedNumberString(inputFieldValue.value)
  emit('change', normalized)
  emit('submit', normalized)
}

const claimsAndSources = useFieldClaimsAndSources(computed(() => props.field))
const shouldShowGroundingClaims = computed(
  () => props.readonly && claimsAndSources.value.claims.length && props.field.manualValue === null,
)

defineExpose({ focus: () => inputRef.value?.focus() })
</script>

<template>
  <MarkdownWithGroundingClaims
    v-if="shouldShowGroundingClaims"
    :value="inputFieldValue"
    :claims="claimsAndSources.claims"
    :class="
      twMerge(
        'h-7 w-full rounded-corner-8 bg-background-transparent px-2 py-[5px] text-sm-12px-light outline-none hover:bg-background-transparent-hovered',
        shouldBeRed ? '[&_p]:!text-text-critical' : 'text-text',
        String($attrs.class || ''),
      )
    "
    readonly
    @show-source="$emit('show-source', $event)"
  />
  <input
    v-else
    ref="inputRef"
    v-model="inputFieldValue"
    :class="
      twMerge(
        'h-7 w-full rounded-corner-8 bg-background-transparent px-2 py-0.5 text-sm-12px-light outline-none focus-within:bg-background-transparent-hovered hover:bg-background-transparent-hovered',
        shouldBeRed ? 'text-text-critical' : 'text-text',
        String($attrs.class || ''),
      )
    "
    inputmode="numeric"
    type="text"
    :readonly="readonly"
    @change="onChange"
  />
</template>
